import { ButtonBase, Stack, Typography } from '@mui/material';
import { Routes } from 'enums';
import PaiButton from 'features/ui/base-components/pai-button';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';

interface Props {
  handleNextStep(): void;

  handleCloseModal(): void;
}

const StartYourJourney: React.FC<Props> = ({ handleNextStep, handleCloseModal }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const options = [
    { label: t('startJourneyActiveSourcingCopilot'), value: 'active_sourcing_copilot' },
    { label: t('startJourneySocialSellingCopilot'), value: 'social_selling_copilot' },
    { label: t('startJourneyNone'), value: 'none' },
  ];

  const [selectedArea, setSelectedArea] = useState(options[0]?.value);

  const handleSelectedAreaChange = (value: string) => {
    return () => {
      setSelectedArea(value);
    };
  };

  const handleSelectProduct = () => {
    switch (selectedArea) {
      case 'active_sourcing_copilot':
        navigate(Routes.RECRUITING_BOOSTER_CO_PILOT_ACCOUNTS);
        handleNextStep();
        break;
      case 'social_selling_copilot':
        navigate(Routes.LEAD_BOOSTER_CO_PILOT_ACCOUNTS);
        handleNextStep();
        break;
      case 'none':
        handleCloseModal();
        break;
      default:
        break;
    }
  };

  return (
    <>
      <img className={classes.image} src={'/dashboard/images/onboarding/onboarding_bg.png'} alt="onboarding" />
      <Stack className={classes.startYourJourney} justifyContent={'center'} alignItems={'center'}>
        <Typography className={classes.title}>{t('startJourneyTitle')}</Typography>
        <Typography className={classes.subTitle}>{t('startJourneySubTitle')}</Typography>
        <div className={classes.card}>
          <Stack gap={2}>
            {options.map((option) => (
              <Stack key={option.value} direction={'row'} gap={3} alignItems={'center'}>
                <ButtonBase onClick={handleSelectedAreaChange(option.value)} disableRipple={true}>
                  <div className={cx(classes.radio, selectedArea === option.value ? 'active' : null)} />
                </ButtonBase>
                <ButtonBase onClick={handleSelectedAreaChange(option.value)} disableRipple={true}>
                  <Typography className={classes.radioText}>{option.label}</Typography>
                </ButtonBase>
              </Stack>
            ))}
          </Stack>
          <div className={classes.buttonContainer}>
            <PaiButton text={t('continue')} size={'medium'} border={'rounded'} fullWidth={false} action={handleSelectProduct} />
          </div>
        </div>
      </Stack>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  startYourJourney: {
    position: 'absolute',
  },
  image: {
    maxWidth: '100%',
    maxHeight: '100%',
    minHeight: '600px',
    width: 'auto',
    height: 'auto',
    borderRadius: '16px',
  },
  title: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 'bold',
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
    marginBottom: '16px',
    maxWidth: '450px',
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(18),
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
    marginBottom: '32px',
  },
  card: {
    background: '#FFF',
    padding: '56px 40px 32px 40px',
    width: '450px',
    boxShadow: Shadows.GREY_SHADOW_LIGHT,
    borderRadius: '10px',
  },
  radio: {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    border: `1px solid ${theme.palette.greys.medium}`,
    transition: '225ms',
    '&.active': {
      border: `1px solid ${theme.palette.primary.main}`,
      background: theme.palette.primary.main,
    },
  },
  radioText: {
    fontSize: theme.typography.pxToRem(20),
  },
  buttonContainer: {
    marginTop: '48px',
    display: 'flex',
    justifyContent: 'center',
  },
}));

export default StartYourJourney;
